import { AFIPFECAEResponse } from '@/features/integrations/domain/model/afip-fecae';
import { OrderComplete } from '@/features/orders/domain/model/order';
import { normalizeOrderForPrint } from '@/features/printing/domain/service/normalize-order-for-print';
import { supabase } from '@/shared/infrastructure/supabase/client';
import { Tables } from '@/shared/infrastructure/supabase/types';

/** URL del driver local de impresión (printer-service). Configurable por entorno. */
export const PRINT_SERVICE_URL = process.env.NEXT_PUBLIC_PRINT_SERVICE_URL ?? 'http://localhost:3030';

/** Timeout por defecto de una operación contra el driver (evita canales colgados en `loading`). */
export const PRINT_TIMEOUT_MS = 10_000;

/** Timeout del chequeo de red (más corto: es una prueba de conexión, no una impresión). */
export const PROBE_TIMEOUT_MS = 4_000;

/**
 * Ejecuta un `fetch` contra el driver local con timeout. Aborta la petición si tarda más de
 * `timeoutMs`; el llamador recibe un `AbortError` que puede convertir en estado de error.
 */
export async function fetchPrintService(path: string, init: RequestInit = {}, timeoutMs = PRINT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(`${PRINT_SERVICE_URL}${path}`, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export interface PrinterInfo {
  ip: string;
  port: number;
  type: 'cashier' | 'kitchen';
}

export interface FiscalPrintOptions {
  order: OrderComplete;
  location: {
    streetname: string;
    streetnumber: string;
    cityname: string;
  };
  afipIntegration: Tables<'afip_integration'>;
  afipFECAEResponse: AFIPFECAEResponse;
}

export interface ProbeTarget {
  ip: string;
  port: number;
}

export interface ProbeResult extends ProbeTarget {
  reachable: boolean;
}

export async function fetchPrintersByType(locationId: string, type: 'cashier' | 'kitchen'): Promise<PrinterInfo[]> {
  const { data: printers, error } = await supabase
    .from('printer')
    .select('ip, port, type')
    .eq('location_id', locationId)
    .eq('type', type);

  if (error) {
    console.error(`Error fetching ${type} printers:`, error);
    return [];
  }

  return printers as PrinterInfo[];
}

export async function fetchAllPrinters(locationId: string): Promise<PrinterInfo[]> {
  const { data: printers, error } = await supabase
    .from('printer')
    .select('ip, port, type')
    .eq('location_id', locationId);

  if (error) {
    console.error(`Error fetching all printers:`, error);
    return [];
  }

  return printers as PrinterInfo[];
}

export async function fetchAfipData(
  locationId: string,
  orderId: string
): Promise<{ integration: Tables<'afip_integration'>; fecae: AFIPFECAEResponse } | null> {
  // 1. Get AFIP Integration
  const { data: afipIntegration, error: afipIntegrationError } = await supabase
    .from('afip_integration')
    .select('*')
    .eq('location_id', locationId)
    .single();

  if (afipIntegrationError || !afipIntegration) {
    return null;
  }

  // 2. Generate FECAE by invoking the edge function
  const responesAFIP = await supabase.functions.invoke<AFIPFECAEResponse>('create-afip-order', {
    body: {
      orderId,
    },
  });

  if (!responesAFIP.data) {
    return null;
  }

  return {
    integration: afipIntegration,
    fecae: responesAFIP.data,
  };
}

export async function fetchExistingAfipOrderData(
  locationId: string,
  orderId: string
): Promise<{ integration: Tables<'afip_integration'>; fecae: AFIPFECAEResponse } | null> {
  const { data: afipIntegration, error: afipIntegrationError } = await supabase
    .from('afip_integration')
    .select('*')
    .eq('location_id', locationId)
    .single();

  if (afipIntegrationError || !afipIntegration) {
    return null;
  }

  const { data: afipOrder, error: afipOrderError } = await supabase
    .from('afip_order')
    .select('*')
    .eq('order_id', orderId)
    .single();

  if (afipOrderError || !afipOrder) {
    return null;
  }

  return {
    integration: afipIntegration,
    fecae: afipOrder.fecae_result as unknown as AFIPFECAEResponse,
  };
}

/** `true` si el local tiene facturación electrónica (AFIP) configurada. No emite comprobante. */
export async function hasAfipIntegration(locationId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('afip_integration')
    .select('id')
    .eq('location_id', locationId)
    .maybeSingle();

  if (error) return false;
  return Boolean(data);
}

/**
 * Pregunta al driver local qué impresoras de la lista responden en la red de esta máquina.
 * Devuelve `null` cuando el driver no soporta `/probe` (versión vieja) o no está disponible,
 * para que el llamador aplique un fallback por subred/heurística.
 */
export async function probePrinters(targets: ProbeTarget[]): Promise<ProbeResult[] | null> {
  if (targets.length === 0) return [];

  try {
    const response = await fetchPrintService(
      '/probe',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targets }),
      },
      PROBE_TIMEOUT_MS
    );

    if (!response.ok) return null;

    const payload = (await response.json().catch(() => null)) as { results?: unknown } | unknown[] | null;
    const rawResults = Array.isArray(payload)
      ? payload
      : payload && typeof payload === 'object' && Array.isArray(payload.results)
        ? payload.results
        : null;

    if (!rawResults) return null;

    return rawResults
      .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
      .map(item => ({
        ip: String(item.ip ?? ''),
        port: Number(item.port ?? 0),
        reachable: Boolean(item.reachable),
      }))
      .filter(item => item.ip);
  } catch {
    return null;
  }
}

export async function printUserTicket(printer: PrinterInfo, order: OrderComplete): Promise<Response> {
  return fetchPrintService('/print', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      printer: {
        ip: printer.ip,
        port: printer.port,
      },
      type: 'ticket',
      order: normalizeOrderForPrint(order),
    }),
  });
}

/**
 * Ticket completo para el tótem físico (carta/pedido público), que imprime contra el driver local
 * del tótem y no contra la cola multicanal del dashboard.
 */
export async function printTotemTicket(printer: { ip: string; port: number }, order: OrderComplete): Promise<Response> {
  return fetchPrintService('/print', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      printer: {
        ip: printer.ip,
        port: printer.port,
      },
      type: 'totem',
      order: normalizeOrderForPrint(order),
    }),
  });
}

export async function printKitchenTicket(printer: PrinterInfo, order: OrderComplete): Promise<Response> {
  return fetchPrintService('/print', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      printer: {
        ip: printer.ip,
        port: printer.port,
      },
      type: 'kitchen',
      order,
    }),
  });
}

export async function printFiscalTicket(printer: PrinterInfo, options: FiscalPrintOptions): Promise<Response> {
  const { order, location, afipIntegration, afipFECAEResponse: FECAE } = options;

  return fetchPrintService('/print', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      printer: {
        ip: printer.ip,
        port: printer.port,
      },
      type: 'afip',
      order: normalizeOrderForPrint(order),
      location: {
        streetname: location.streetname ?? 'Sin calle',
        streetnumber: location.streetnumber ?? 'Sin número',
        cityname: location.cityname ?? 'Sin ciudad',
      },
      afipIntegration: {
        company: afipIntegration.company,
        cuit: afipIntegration.cuit,
        responsible: afipIntegration.responsible,
        startDate: afipIntegration.start_at ? new Date(afipIntegration.start_at) : undefined,
      },
      afipFECAEResponse: {
        feCabResp: {
          cuit: FECAE.FeCabResp?.Cuit || '',
          ptoVta: FECAE.FeCabResp?.PtoVta || 0,
          cbteTipo: FECAE.FeCabResp?.CbteTipo || 0,
          fchProceso: FECAE.FeCabResp?.FchProceso || '',
          cantReg: FECAE.FeCabResp?.CantReg || 0,
          resultado: FECAE.FeCabResp?.Resultado || '',
          reproceso: FECAE.FeCabResp?.Reproceso || '',
        },
        feDetResp: {
          fecaeDetResponse:
            FECAE.FeDetResp?.FECAEDetResponse?.map(item => ({
              concepto: item.Concepto || 0,
              docTipo: item.DocTipo || 0,
              docNro: item.DocNro || '',
              cbteDesde: item.CbteDesde || '',
              cbteHasta: item.CbteHasta || '',
              cbteFch: item.CbteFch || '',
              resultado: item.Resultado || '',
              cae: item.CAE || '',
              caeFchVto: item.CAEFchVto || '',
              observaciones: item.Observaciones || null,
            })) || [],
        },
        errors: FECAE.Errors || null,
      },
    }),
  });
}
